import { useEffect, useRef } from "react";

export function MatrixBackground() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let frame = 0;
    let width = 0;
    let height = 0;
    let time = 0;
    const pointer = { x: -1000, y: -1000, tx: -1000, ty: -1000 };
    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      a: number;
      s: number;
      pulse: number;
    }> = [];
    let streams: Array<{
      x: number;
      y: number;
      speed: number;
      alpha: number;
      size: number;
      char: string;
    }> = [];
    const symbols = "01G<>/{}[]+*";
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const resize = () => {
      const ratio = Math.min(devicePixelRatio, 2);
      width = innerWidth;
      height = innerHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      particles = Array.from(
        { length: Math.min(110, Math.round(width / 13)) },
        () => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.12,
          vy: 0.08 + Math.random() * 0.18,
          a: 0.14 + Math.random() * 0.24,
          s: 0.5 + Math.random() * 1.4,
          pulse: Math.random() * Math.PI * 2,
        }),
      );
      streams = Array.from(
        { length: Math.min(42, Math.round(width / 34)) },
        () => ({
          x: Math.random() * width,
          y: Math.random() * height,
          speed: 0.18 + Math.random() * 0.38,
          alpha: 0.045 + Math.random() * 0.12,
          size: 9 + Math.random() * 5,
          char: symbols[Math.floor(Math.random() * symbols.length)]!,
        }),
      );
    };
    const draw = (still = false) => {
      time += still ? 0 : 0.012;
      ctx.clearRect(0, 0, width, height);
      pointer.x += (pointer.tx - pointer.x) * 0.035;
      pointer.y += (pointer.ty - pointer.y) * 0.035;

      const wash = ctx.createRadialGradient(
        pointer.x,
        pointer.y,
        0,
        pointer.x,
        pointer.y,
        Math.max(260, width * 0.34),
      );
      wash.addColorStop(0, "rgba(79,255,124,.09)");
      wash.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = wash;
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = "rgba(79,255,124,.032)";
      ctx.lineWidth = 1;
      const gridOffset = (time * 14) % 48;
      for (let x = -48 + gridOffset; x < width + 48; x += 48) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x - 120, height);
        ctx.stroke();
      }

      ctx.font = "11px Space Mono, monospace";
      for (const stream of streams) {
        if (!still) stream.y += stream.speed;
        if (stream.y > height + 20) {
          stream.y = -20;
          stream.char = symbols[Math.floor(Math.random() * symbols.length)]!;
        }
        ctx.fillStyle = `rgba(88,255,132,${stream.alpha})`;
        ctx.fillText(stream.char, stream.x, stream.y);
        ctx.fillStyle = `rgba(88,255,132,${stream.alpha * 0.32})`;
        ctx.fillText(stream.char, stream.x, stream.y - stream.size * 1.8);
      }

      ctx.globalCompositeOperation = "lighter";
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i]!;
        if (!still) {
          p.x += p.vx + Math.sin(time + p.pulse) * 0.03;
          p.y += p.vy;
        }
        if (p.y > height + 10) {
          p.y = -10;
          p.x = Math.random() * width;
        }
        ctx.beginPath();
        const pulse = 0.72 + Math.sin(time * 2 + p.pulse) * 0.28;
        ctx.fillStyle = `rgba(72,255,122,${p.a * pulse})`;
        ctx.shadowBlur = 10;
        ctx.shadowColor = "rgba(63,255,112,.4)";
        ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2);
        ctx.fill();
        for (let j = i + 1; j < Math.min(particles.length, i + 12); j += 1) {
          const other = particles[j]!;
          const dx = p.x - other.x;
          const dy = p.y - other.y;
          const distance = Math.hypot(dx, dy);
          if (distance < 92) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(79,255,124,${(1 - distance / 92) * 0.06})`;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }
      }
      ctx.globalCompositeOperation = "source-over";
      ctx.shadowBlur = 0;
      if (!still) frame = requestAnimationFrame(() => draw());
    };
    const move = (event: PointerEvent) => {
      pointer.tx = event.clientX;
      pointer.ty = event.clientY;
    };
    resize();
    if (!reduced) draw();
    else draw(true);
    addEventListener("resize", resize);
    addEventListener("pointermove", move, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("resize", resize);
      removeEventListener("pointermove", move);
    };
  }, []);
  return <canvas ref={ref} className="matrix-canvas" aria-hidden="true" />;
}
